import React, { createContext, useContext, useState } from 'react'
import { DateTime } from 'luxon'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  FormHelperText,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import DialogHeader from '../components/DialogHeader'
import { useDisabledInPreview } from '../dashboard/panelPreviewContext'
import { advancedColors } from '../theme'

export { todayIso } from '../format'

// For a disabled field whose prop type still demands an `onChange`.
export const noop = (): void => undefined

export const LabeledText = ({
  label,
  value,
  onChange,
  required,
  multiline,
  minRows,
  disabled,
  type = 'text',
  helperText,
  placeholder,
  error,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  multiline?: boolean
  minRows?: number
  disabled?: boolean
  type?: string
  helperText?: string
  placeholder?: string
  error?: string
}) => (
  <TextField
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    required={required}
    error={Boolean(error)}
    multiline={multiline}
    minRows={multiline ? minRows ?? 3 : undefined}
    disabled={useDisabledInPreview(disabled)}
    type={type}
    helperText={error ?? helperText}
    placeholder={placeholder}
    size="small"
    fullWidth
  />
)

export const REQUIRED = 'Required'

export const SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { sx: { maxHeight: 320 } },
} as const

export const LabeledSelect = <T extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
  required,
  error,
}: {
  label: string
  value: T | ''
  onChange: (value: T) => void
  options: { value: T; label: string }[]
  disabled?: boolean
  required?: boolean
  error?: string
}) => (
  <TextField
    select
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value as T)}
    disabled={useDisabledInPreview(disabled)}
    required={required}
    error={Boolean(error)}
    helperText={error}
    size="small"
    fullWidth
    SelectProps={{ MenuProps: SELECT_MENU_PROPS, sx: { '& .MuiSelect-select': { whiteSpace: 'normal' } } }}
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value} sx={{ whiteSpace: 'normal' }}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
)

// Like `LabeledSelect`, but for a field whose value is a set of options rather than one.
export const LabeledMultiSelect = <T extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string
  value: T[]
  onChange: (value: T[]) => void
  options: { value: T; label: string }[]
  disabled?: boolean
}) => (
  <TextField
    select
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value as unknown as T[])}
    disabled={useDisabledInPreview(disabled)}
    size="small"
    fullWidth
    SelectProps={{
      MenuProps: SELECT_MENU_PROPS,
      multiple: true,
      renderValue: (selected) =>
        options
          .filter((option) => (selected as T[]).includes(option.value))
          .map((option) => option.label)
          .join(', '),
    }}
  >
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        <Checkbox checked={value.includes(option.value)} size="small" />
        <ListItemText primary={option.label} />
      </MenuItem>
    ))}
  </TextField>
)

export const LabeledNumber = ({
  label,
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder,
  helperText,
  required,
  error,
}: {
  label: string
  value: number | ''
  onChange: (value: number | '') => void
  min?: number
  max?: number
  disabled?: boolean
  placeholder?: string
  helperText?: string
  required?: boolean
  error?: string
}) => (
  <TextField
    label={label}
    required={required}
    error={Boolean(error)}
    value={value}
    onChange={(event) => {
      const raw = event.target.value
      if (raw === '') {
        onChange('')
        return
      }
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) {
        onChange(parsed)
      }
    }}
    type="number"
    inputProps={{ min, max }}
    placeholder={placeholder}
    helperText={error ?? helperText}
    disabled={useDisabledInPreview(disabled)}
    size="small"
    fullWidth
  />
)

// Standard's pickers show month/day/year whatever the browser's locale, so these do too.
const DATE_FORMAT = 'MM/dd/yyyy'

const validDate = (value: string) => {
  const parsed = value ? DateTime.fromISO(value) : null

  return parsed?.isValid ? parsed : null
}

export const LabeledDate = ({
  label,
  value,
  onChange,
  required,
  error,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  error?: string
}) => (
  <LocalizationProvider dateAdapter={AdapterLuxon}>
    <DesktopDatePicker
      label={label}
      inputFormat={DATE_FORMAT}
      value={validDate(value)}
      onChange={(next) => onChange(next?.isValid ? next.toISODate() ?? '' : '')}
      disabled={useDisabledInPreview()}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={params.error || Boolean(error)}
          helperText={error}
          size="small"
          fullWidth
        />
      )}
    />
  </LocalizationProvider>
)

export const LabeledTime = ({
  label,
  value,
  onChange,
  required,
  error,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  error?: string
}) => (
  <LocalizationProvider dateAdapter={AdapterLuxon}>
    <DesktopTimePicker
      ampm
      label={label}
      value={value ? DateTime.fromFormat(value, 'HH:mm') : null}
      onChange={(next) => onChange(next?.isValid ? next.toFormat('HH:mm') : '')}
      disabled={useDisabledInPreview()}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={params.error || Boolean(error)}
          helperText={error}
          size="small"
          fullWidth
        />
      )}
    />
  </LocalizationProvider>
)

export const LabeledCheckbox = ({
  label,
  checked,
  onChange,
  helperText,
  required,
  error,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  helperText?: string
  required?: boolean
  error?: string
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
      <FormControlLabel
        sx={required ? { mr: 0.5 } : undefined}
        control={
          <Checkbox
            checked={checked}
            disabled={useDisabledInPreview()}
            onChange={(event) => onChange(event.target.checked)}
          />
        }
        label={label}
      />
      {required && (
        <Typography component="span" aria-hidden sx={{ color: advancedColors.error }}>
          *
        </Typography>
      )}
    </Box>
    {(error ?? helperText) && (
      <FormHelperText error={Boolean(error)} sx={{ mt: -0.5, ml: 4 }}>
        {error ?? helperText}
      </FormHelperText>
    )}
  </Box>
)

export const LabeledRadioGroup = <T extends string>({
  label,
  value,
  onChange,
  options,
  required,
  error,
}: {
  label: string
  value: T | ''
  onChange: (value: T) => void
  options: { value: T; label: string }[]
  required?: boolean
  error?: string
}) => {
  const disabled = useDisabledInPreview()

  if (options.length > 2) {
    return (
      <LabeledSelect
        label={label || 'Select one'}
        value={value}
        onChange={onChange}
        options={options}
        required={required}
        error={error}
      />
    )
  }

  return (
    <Box>
      {label && (
        <Typography variant="overline">
          {label}
          {required && ' *'}
        </Typography>
      )}
      <RadioGroup value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" disabled={disabled} />}
            label={option.label}
          />
        ))}
      </RadioGroup>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  )
}

export const QuestionNumber = ({
  question,
  value,
  onChange,
  min,
  max,
  disabled,
  placeholder,
  required,
  error,
}: {
  question: string
  value: number | ''
  onChange: (value: number | '') => void
  min?: number
  max?: number
  disabled?: boolean
  placeholder?: string
  required?: boolean
  error?: string
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) 140px' },
      columnGap: 2,
      rowGap: 0.5,
      alignItems: 'center',
    }}
  >
    <Typography variant="body2" sx={{ color: advancedColors.ink }}>
      {question}
      {required && ' *'}
    </Typography>
    <TextField
      value={value}
      onChange={(event) => {
        const raw = event.target.value

        if (raw === '') {
          onChange('')
        } else if (Number.isFinite(Number(raw))) {
          onChange(Number(raw))
        }
      }}
      type="number"
      inputProps={{ min, max, 'aria-label': question }}
      placeholder={placeholder}
      error={Boolean(error)}
      helperText={error}
      disabled={useDisabledInPreview(disabled)}
      size="small"
      fullWidth
      sx={{ minWidth: 0, '& input': { minWidth: 0 } }}
    />
  </Box>
)

export const CheckboxGroup = ({
  title,
  options,
  values,
  onChange,
}: {
  title?: string
  options: { key: string; label: string; hint?: string }[]
  values: Record<string, boolean>
  onChange: (key: string, checked: boolean) => void
}) => (
  <Box>
    {title && <Typography variant="overline">{title}</Typography>}
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {options.map((option) => (
        <LabeledCheckbox
          key={option.key}
          label={option.label}
          checked={Boolean(values[option.key])}
          onChange={(checked) => onChange(option.key, checked)}
          helperText={option.hint}
        />
      ))}
    </Box>
  </Box>
)

const emptyCheckboxGroup = (options: { key: string }[]): Record<string, boolean> =>
  Object.fromEntries(options.map((option) => [option.key, false]))

// Pass `initial` as `{}` where unset keys should stay absent rather than becoming explicit `false`.
export const useCheckboxGroup = (
  options: { key: string }[],
  initial?: Record<string, boolean>
): [Record<string, boolean>, (key: string, checked: boolean) => void] => {
  const [values, setValues] = useState<Record<string, boolean>>(() => initial ?? emptyCheckboxGroup(options))
  const toggle = (key: string, checked: boolean) => setValues((current) => ({ ...current, [key]: checked }))
  return [values, toggle]
}

// Submits as `{ ...values, OTHER: other }`.
export const useCheckboxGroupWithOther = (
  options: { key: string }[],
  initial?: { values?: Record<string, boolean>; other?: string }
): {
  values: Record<string, boolean>
  toggle: (key: string, checked: boolean) => void
  other: string
  setOther: (value: string) => void
  merged: Record<string, boolean | string>
} => {
  const [values, toggle] = useCheckboxGroup(options, initial?.values)
  const [other, setOther] = useState(initial?.other ?? '')
  return { values, toggle, other, setOther, merged: { ...values, OTHER: other } }
}

export const CheckboxGroupWithOther = ({
  title,
  options,
  state,
  otherLabel,
}: {
  title?: string
  options: { key: string; label: string }[]
  state: ReturnType<typeof useCheckboxGroupWithOther>
  otherLabel: string
}) => (
  <>
    <CheckboxGroup title={title} options={options} values={state.values} onChange={state.toggle} />
    <LabeledText label={otherLabel} value={state.other} onChange={state.setOther} multiline />
  </>
)

const SectionDepthContext = createContext(0)

export const FieldSection = ({ title, children }: { title?: string; children: React.ReactNode }) => {
  const depth = useContext(SectionDepthContext)
  const fields = <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{children}</Box>

  if (!title) {
    return fields
  }

  const top = depth === 0

  return (
    <SectionDepthContext.Provider value={depth + 1}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          ...(top && {
            mt: 1.5,
            pt: 2.5,
            borderTop: `1px solid ${advancedColors.line}`,
            '&:first-of-type': { mt: 0, pt: 0, borderTop: 'none' },
          }),
        }}
      >
        <Typography
          component="h3"
          sx={{ fontSize: top ? '1.25rem' : '1rem', fontWeight: 700, lineHeight: 1.3, color: advancedColors.ink }}
        >
          {title}
        </Typography>
        {fields}
      </Box>
    </SectionDepthContext.Provider>
  )
}

export const FieldGrid = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: 2,
      '& > *': { minWidth: 0 },
    }}
  >
    {children}
  </Box>
)

export const FieldRow = ({ columns, children }: { columns: number; children: React.ReactNode }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 2 }}>{children}</Box>
)

export const LabeledValue = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="overline">{label}</Typography>
    <Typography variant="body2" sx={{ mt: 0.25, color: advancedColors.ink, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
)

export const FieldError = ({ message }: { message: string | null }) =>
  message ? (
    <Typography variant="body2" sx={{ color: advancedColors.error }}>
      {message}
    </Typography>
  ) : null

// Add stays pressable so a failed attempt can say what is missing, not a disabled button with no reason.
export const useRequiredFields = (filled: Record<string, boolean>) => {
  const [attempted, setAttempted] = useState(false)
  const missing = Object.keys(filled).filter((label) => !filled[label])

  return {
    missing: attempted ? missing : [],
    error: (label: string) => (attempted && filled[label] === false ? REQUIRED : undefined),
    attempt: () => {
      setAttempted(true)

      return missing.length === 0
    },
    reset: () => setAttempted(false),
  }
}

export const MissingFields = ({ labels }: { labels: string[] }) =>
  labels.length === 0 ? null : (
    <Box role="alert">
      <FieldError message={`Required: ${labels.join(', ')}`} />
    </Box>
  )

export const FieldStatus = ({ loading, status }: { loading?: boolean; status?: 'idle' | 'saving' | 'saved' }) => {
  if (loading) {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
        Loading patient record…
      </Typography>
    )
  }

  if (status === 'saving') {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
        Saving…
      </Typography>
    )
  }

  if (status === 'saved') {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.green }}>
        Saved
      </Typography>
    )
  }

  return null
}

export const CardFooter = ({
  loading,
  status,
  error,
}: {
  loading?: boolean
  status?: 'idle' | 'saving' | 'saved'
  error: string | null
}) => (
  <>
    <FieldStatus loading={loading} status={status} />
    <FieldError message={error} />
  </>
)

const CONFIRM_CONTENTS =
  "You're about to finalize the note, and it will become a formal, uneditable part of the chart. Are you sure you want to proceed?"

export type ConfirmNote = { buttonLabel: string; title: string }

export const FormActions = ({
  onSubmit,
  disabled,
  submitting,
  error,
  confirm,
}: {
  onSubmit: () => void
  disabled: boolean
  submitting: boolean
  error: Error | null | undefined
  confirm?: ConfirmNote
}) => {
  const [confirming, setConfirming] = useState(false)
  const unavailable = useDisabledInPreview(disabled || submitting)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
      <FieldError message={error?.message ?? null} />
      <Button variant="contained" onClick={confirm ? () => setConfirming(true) : onSubmit} disabled={unavailable}>
        {confirm?.buttonLabel ?? (submitting ? 'Adding…' : 'Add')}
      </Button>
      {confirm && (
        <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
          <DialogHeader title={confirm.title} onClose={() => setConfirming(false)} />
          <DialogContent>
            <DialogContentText>{CONFIRM_CONTENTS}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={unavailable}
              onClick={() => {
                setConfirming(false)
                onSubmit()
              }}
            >
              Proceed
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}

export const DraftNotice = ({ restored, dirty }: { restored: boolean; dirty: boolean }) =>
  restored || dirty ? <Alert severity="info">{dirty ? 'Draft saved.' : 'Draft restored.'}</Alert> : null
